import { Grid, Typography } from "@mui/material";
import FormControl from "@mui/material/FormControl";
import type { FormControlLabelProps } from "@mui/material/FormControlLabel";
import FormControlLabel from "@mui/material/FormControlLabel";
import { makeStyles } from "@mui/styles";
import FormHelperText from "@mui/material/FormHelperText";
import Radio from "@mui/material/Radio";
import type { RadioGroupProps } from "@mui/material/RadioGroup";
import RadioGroup from "@mui/material/RadioGroup";
import { ReactNode } from "react";
import type { Control, FieldPath, FieldValues } from "react-hook-form";
import { Controller } from "react-hook-form";
import { FontSize } from "src/const/font";
import useCheckMobile from "src/hook/useCheckMobile";


export interface Option {
  value: any;
  label: ReactNode;
  subContent?: ReactNode;
  disabled?: boolean;
  positionSubContent?: string;
}

interface Props<T extends FieldValues>
  extends Omit<FormControlLabelProps, "control" | "label"> {
  control: Control<T>;
  name: FieldPath<T>;
  options: Option[];
  row?: RadioGroupProps["row"];
  onChangeSelect?: (value: number | null) => void;
  singleSubContentMode?: boolean;
  containerStyle?: React.CSSProperties;
  contentContainer?: React.CSSProperties;
}

const useStyles = makeStyles((theme) => {
  return {
    textDisabled: {
      width: "100%",
      fontSize: FontSize.normal.default,
      display: "flex",
      [theme.breakpoints.down("md")]: {
        fontSize: FontSize.normal.p4,
      },
      "& .MuiFormControlLabel-root": {
        width: "100%",
      },
      "& .Mui-disabled": {
        color: "rgba(0, 0, 0, 0.1)",
      },
      color: "rgba(0, 0, 0, 0.26)",
    },
    styleDefault: {
      width: "100%",
      fontSize: FontSize.normal.default,
      display: "flex",
      color: theme.custom?.text.title,
      [theme.breakpoints.down("md")]: {
        fontSize: FontSize.normal.p4,
      },
      "& .MuiFormControlLabel-root": {
        width: "100%",
      },
      "& .Mui-disabled": {
        color: "rgba(0, 0, 0, 0.1)",
      }

    },
   
  };
});

const ControllerRadio = <T extends FieldValues>(props: Props<T>) => {
  const isMobile = useCheckMobile()
  const {
    control,
    name,
    options,
    row,
    onChangeSelect,
    singleSubContentMode = true,
    containerStyle,
    contentContainer,
    ...rest
  } = props;
  const classes = useStyles();

  return (
    <Controller
      render={({ field, fieldState: { error } }) => (
        <FormControl error={Boolean(error)} style={{ width: "100%" }}>
          <RadioGroup
            style={{ ...containerStyle }}
            row={row}
            {...field}
            className={classes.styleDefault}
            onChange={(_event, value: any) => {
              field.onChange(value);
              if (onChangeSelect) {
                if (typeof value === "string") {
                  onChangeSelect(parseInt(value));
                } else {
                  onChangeSelect(value);
                }
              }
            }}
          >
            {options.map(
              ({
                value,
                label,
                positionSubContent = "bottom",
                subContent,
                disabled,
              }) => (
                <div style={{ ...contentContainer }}>
                  {value && (
                    <div
                      style={
                        positionSubContent === "right"
                          ? {
                            display: "flex",
                            justifyContent: "space-between",
                            alignItems: isMobile ? 'center' : 'flex-start',
                            width: "100%",
                          }
                          : { width: "100%", display: "contents" }
                      }
                    >
                      <FormControlLabel
                        {...rest}
                        key={value}
                        value={value}
                        disabled={disabled}
                        control={
                            <Radio
                              size="small"
                              sx={{
                                "& .Mui-disabled": {
                                  color: "red",
                                },
                                '&.Mui-checked': {
                                  color : '#DD0071',
                                },
                              }}
                            />

                        }
                        sx={{
                          "& .MuiTypography-root": {
                            width: "100%",
                            textAlign: "left",
                          },
                        }}
                        label={
                          <div className={` ${disabled ? classes.textDisabled : classes.styleDefault}`}>{label}</div>
                        }
                      />
                      {singleSubContentMode
                        ? control._formValues[name] == value
                          ? subContent
                          : null
                        : subContent}
                    </div>
                  )}
                </div>
              )
            )}
          </RadioGroup>
          <FormHelperText variant="standard">{error?.message}</FormHelperText>
        </FormControl>
      )}
      name={name}
      control={control}
    />
  );
};

export default ControllerRadio;
